<script setup lang="ts">
import { ROUTE_NAMES } from '~/constants/routes';

// COMPOSABLES
const { tt } = useTypedI18n();
const config = useRuntimeConfig();
const localePath = useLocalePath();
const { phones } = useFleequidContacts();

// LIFECYCLE
onMounted(() => {
    const focusedElement = ((document.hasFocus() &&
        document.activeElement !== document.body &&
        document.activeElement !== document.documentElement &&
        document.activeElement) ||
        null) as HTMLElement | null;
    focusedElement?.blur();
});
</script>

<template>
    <ContentPage :title="tt('faq.title')">
        <FaqSection :title="tt('faq.start.base')">
            <FaqItem slug="participate-in-auctions" :question="tt('faq.start.participate.q')">
                <i18n-t tag="p" scope="global" keypath="faq.start.participate.a">
                    <NuxtLinkLocale class="text-primary hover:underline" to="signup" target="_blank">
                        {{ tt('faq.start.participate.link') }}
                    </NuxtLinkLocale>
                </i18n-t>
            </FaqItem>
            <FaqItem slug="reserve-price" :question="tt('faq.start.reservePrice.q')">
                <p>{{ tt('faq.start.reservePrice.a') }}</p>
            </FaqItem>
            <FaqItem slug="countries" :question="tt('faq.start.countries.q')">
                <i18n-t tag="p" scope="global" keypath="faq.start.countries.a">
                    <FleequidEmailLink type="info" />
                </i18n-t>
            </FaqItem>
            <FaqItem slug="bureaucracy" :question="tt('faq.start.bureaucracy.q')">
                <p>{{ tt('faq.start.bureaucracy.a') }}</p>
            </FaqItem>
            <FaqItem slug="scrapping" :question="tt('faq.start.toBeScrapped.q')">
                <p>{{ tt('faq.start.toBeScrapped.a') }}</p>
            </FaqItem>
            <FaqItem slug="vies" :question="tt('faq.start.vies.q')">
                <p>{{ tt('faq.start.vies.a') }}</p>
            </FaqItem>
            <FaqItem slug="coc" :question="tt('faq.start.coc.q')">
                <p>{{ tt('faq.start.coc.a') }}</p>
            </FaqItem>
            <FaqItem slug="buy-now-what" :question="tt('faq.start.buyNowWhat.q')">
                <p>{{ tt('faq.start.buyNowWhat.a') }}</p>
            </FaqItem>
            <FaqItem slug="buy-now-how" :question="tt('faq.start.buyNowHow.q')">
                <p>{{ tt('faq.start.buyNowHow.a') }}</p>
            </FaqItem>
            <FaqItem slug="buy-now-how-to-buy" :question="tt('faq.start.buyNowHowToBuy.q')">
                <p>{{ tt('faq.start.buyNowHowToBuy.a') }}</p>
            </FaqItem>
            <FaqItem slug="buy-now-negotiate" :question="tt('faq.start.buyNowNegotiate.q')">
                <p>{{ tt('faq.start.buyNowNegotiate.a') }}</p>
            </FaqItem>
            <FaqItem slug="buy-now-who-can-negotiate" :question="tt('faq.start.buyNowWhoCanNegotiate.q')">
                <p>{{ tt('faq.start.buyNowWhoCanNegotiate.a') }}</p>
            </FaqItem>
        </FaqSection>

        <FaqSection :title="tt('faq.sell.base')">
            <FaqItem slug="start-selling" :question="tt('faq.sell.selling.q')">
                <i18n-t tag="p" scope="global" keypath="faq.sell.selling.a">
                    <FleequidEmailLink type="info" />
                    <NuxtLinkLocale class="text-primary hover:underline" :to="ROUTE_NAMES.SELL">
                        {{ config.public.frontendBaseUrl }}{{ localePath(ROUTE_NAMES.SELL) }}
                    </NuxtLinkLocale>
                </i18n-t>
            </FaqItem>
            <FaqItem slug="starting-price" :question="tt('faq.sell.startingPrice.q')">
                <p>{{ tt('faq.sell.startingPrice.a') }}</p>
            </FaqItem>
            <FaqItem slug="delivery" :question="tt('faq.sell.delivery.q')">
                <p>{{ tt('faq.sell.delivery.a') }}</p>
            </FaqItem>
            <FaqItem slug="how-long-to-sell" :question="tt('faq.sell.howLong.q')">
                <p>{{ tt('faq.sell.howLong.a') }}</p>
            </FaqItem>
            <FaqItem slug="expired-inspection" :question="tt('faq.sell.expiredInspection.q')">
                <p>{{ tt('faq.sell.expiredInspection.a') }}</p>
            </FaqItem>
        </FaqSection>

        <FaqSection :title="tt('faq.buy.base')">
            <FaqItem slug="bus-inspection" :question="tt('faq.buy.inspection.q')">
                <p>{{ tt('faq.buy.inspection.a') }}</p>
            </FaqItem>
            <FaqItem slug="hidden-defects-and-claims" :question="tt('faq.buy.claims.q')">
                <p>{{ tt('faq.buy.claims.a') }}</p>
            </FaqItem>
            <FaqItem slug="payment-methods" :question="tt('faq.buy.paymentMethods.q')">
                <p>{{ tt('faq.buy.paymentMethods.a') }}</p>
            </FaqItem>
            <FaqItem slug="how-payment-works" :question="tt('faq.buy.howPaymentWorks.q')">
                <p>{{ tt('faq.buy.howPaymentWorks.a') }}</p>
            </FaqItem>
            <FaqItem slug="deposit" :question="tt('faq.buy.deposit.q')">
                <p>{{ tt('faq.buy.deposit.a') }}</p>
            </FaqItem>
            <FaqItem slug="auto-bid" :question="tt('faq.buy.autoBid.q')">
                <p class="mb-4">{{ tt('faq.buy.autoBid.a1') }}</p>
                <p class="mb-4">{{ tt('faq.buy.autoBid.a2') }}</p>
                <p class="mb-4">{{ tt('faq.buy.autoBid.a3') }}</p>
                <p class="mb-4">{{ tt('faq.buy.autoBid.a4') }}</p>
                <p>{{ tt('faq.buy.autoBid.a5') }}</p>
            </FaqItem>
            <FaqItem slug="negotiation" :question="tt('faq.buy.auctionNegotiation.q')">
                <p class="mb-4">{{ tt('faq.buy.auctionNegotiation.a1') }}</p>
                <p class="mb-4">{{ tt('faq.buy.auctionNegotiation.a2') }}</p>

                <p>{{ tt('faq.buy.auctionNegotiation.a3') }}</p>
                <ul class="mb-4 list-disc pl-4">
                    <i18n-t scope="global" tag="li" keypath="faq.buy.auctionNegotiation.a3_1">
                        <NuxtLinkLocale class="text-primary hover:underline" :to="ROUTE_NAMES.PRICING_BUYER" target="_blank">
                            <span>{{ tt('faq.buy.auctionNegotiation.fee') }}</span>
                        </NuxtLinkLocale>
                    </i18n-t>
                    <i18n-t scope="global" tag="li" keypath="faq.buy.auctionNegotiation.a3_2">
                        <NuxtLinkLocale class="text-primary hover:underline" :to="ROUTE_NAMES.PRICING_SELLER" target="_blank">
                            <span>{{ tt('faq.buy.auctionNegotiation.fee') }}</span>
                        </NuxtLinkLocale>
                    </i18n-t>
                </ul>

                <p>{{ tt('faq.buy.auctionNegotiation.a4') }}</p>
                <ul class="mb-4 list-disc pl-4">
                    <li>{{ tt('faq.buy.auctionNegotiation.a4_1') }}</li>
                    <li>{{ tt('faq.buy.auctionNegotiation.a4_2') }}</li>
                </ul>

                <p>{{ tt('faq.buy.auctionNegotiation.a5') }}</p>
                <ul class="mb-4 list-disc pl-4">
                    <li>{{ tt('faq.buy.auctionNegotiation.a5_1') }}</li>
                    <li>{{ tt('faq.buy.auctionNegotiation.a5_2') }}</li>
                </ul>

                <p class="mb-4">{{ tt('faq.buy.auctionNegotiation.a6') }}</p>
            </FaqItem>
        </FaqSection>

        <FaqSection :title="tt('faq.valueAddedServices.base')">
            <FaqItem slug="additional-services" :question="tt('faq.valueAddedServices.additionalServices.q')">
                <p>{{ tt('faq.valueAddedServices.additionalServices.a') }}</p>
            </FaqItem>
            <FaqItem slug="available-services" :question="tt('faq.valueAddedServices.availableServices.q')">
                <p>{{ tt('faq.valueAddedServices.availableServices.a1') }}</p>
                <ol class="mb-4 list-decimal pl-4">
                    <li>{{ tt('faq.valueAddedServices.availableServices.a2') }}</li>
                    <li>{{ tt('faq.valueAddedServices.availableServices.a3') }}</li>
                    <li>{{ tt('faq.valueAddedServices.availableServices.a4') }}</li>
                    <li>{{ tt('faq.valueAddedServices.availableServices.a5') }}</li>
                </ol>
            </FaqItem>
            <FaqItem slug="how-to-request" :question="tt('faq.valueAddedServices.howToRequest.q')">
                <p>{{ tt('faq.valueAddedServices.howToRequest.a1') }}</p>
                <ul class="mb-4 list-disc pl-4">
                    <li>{{ tt('faq.valueAddedServices.howToRequest.a2') }}</li>
                    <li>{{ tt('faq.valueAddedServices.howToRequest.a3') }}</li>
                    <li>{{ tt('faq.valueAddedServices.howToRequest.a4') }}</li>
                </ul>
            </FaqItem>
            <FaqItem slug="when-to-add" :question="tt('faq.valueAddedServices.whenToAdd.q')">
                <p>{{ tt('faq.valueAddedServices.whenToAdd.a1') }}</p>
                <ul class="mb-4 list-disc pl-4">
                    <li>{{ tt('faq.valueAddedServices.whenToAdd.a2') }}</li>
                    <li>{{ tt('faq.valueAddedServices.whenToAdd.a3') }}</li>
                    <li>{{ tt('faq.valueAddedServices.whenToAdd.a4') }}</li>
                    <li>{{ tt('faq.valueAddedServices.whenToAdd.a5') }}</li>
                </ul>
            </FaqItem>
            <FaqItem slug="vas-costs" :question="tt('faq.valueAddedServices.costs.q')">
                <p>{{ tt('faq.valueAddedServices.costs.a') }}</p>
            </FaqItem>
            <FaqItem slug="vas-payment" :question="tt('faq.valueAddedServices.paymentManagement.q')">
                <p>{{ tt('faq.valueAddedServices.paymentManagement.a') }}</p>
            </FaqItem>
            <FaqItem slug="vas-status" :question="tt('faq.valueAddedServices.statusTracking.q')">
                <p>{{ tt('faq.valueAddedServices.statusTracking.a') }}</p>
            </FaqItem>
            <FaqItem slug="vas-unavailable" :question="tt('faq.valueAddedServices.unavailableService.q')">
                <p>{{ tt('faq.valueAddedServices.unavailableService.a') }}</p>
            </FaqItem>
        </FaqSection>

        <i18n-t scope="global" tag="p" :keypath="'faq.contactUs'">
            <span class="inline-flex gap-1">
                <BasePhoneLink v-for="phone in phones" :key="phone" :phone="phone" />
            </span>
            <FleequidEmailLink type="customerService" />
        </i18n-t>
    </ContentPage>
</template>
